import moment from "moment";
import { DATE_FORMATS } from "../../../../../../constants";
import { DurationUnit } from "../../../../../../types";
import { mutateErrors } from "../../../../../../utils";
import {
  WIZARD_ERROR_MESSAGE,
} from "../../../../../wizard/constants";
import { WizardMode, TValidator } from "../../../../../wizard/types";
import { ProgramType } from "../../../../constants";
import { parseToCheckDuplicationModel } from "../../../validators/parseToCheckDuplicationModel";
import { validateIntakeGroup } from "../../../validators/validators";
import { isPositiveNumber } from "../../../../../../helpers";

type CommonObject = { [key: string]: any };

const validator: TValidator = async ({
  values,
  activeStepName,
  backendValidator,
}) => {
  const errors: CommonObject = {};
  const notifyMessages: string[] = [];

  errors.existingIntakes = [];
  errors.defaultNewIntakes = [];
  errors.newIntakes = [];

  const stepValues = values[activeStepName] ?? {};
  const isCreateMode = values?.mode === WizardMode.Create;

  // Program Type -----------------------------------------------
  if (!stepValues.programType) {
    errors.programType = "Select an option";
  }

  // Program Duration -----------------------------------------------
  if (!stepValues.durationUnit) {
    errors.durationUnit = "Duration period is required";
  }

  if (stepValues?.durationUnit?.id !== DurationUnit.FULL) {
    if (!stepValues.durationAmount?.trim()) {
      errors.durationAmount = "Duration is required";
    } else if (!isPositiveNumber(stepValues.durationAmount)) {
      errors.durationAmount = "Duration is not valid";
    } else if (stepValues.durationAmount >= 10000) {
      errors.durationAmount = "Max 4 digits allowed";
    } else if (stepValues.durationAmount.trim().split(".")[1]?.length > 2) {
      errors.durationAmount = "Max 2 digits allowed after decimal point";
    }
  }

  // Housing Type -------------------------------------------------
  if (
    stepValues.programType === ProgramType.ON_CAMPUS &&
    !stepValues.housingTypes?.length
  ) {
    errors.housingTypes = "Housing type is required"; //todo correct message text
  }

  // Intake -------------------------------------------------------
  const allIntakes = [
    ...(stepValues.existingIntakes || []),
    ...(stepValues.defaultNewIntakes || []),
    ...(stepValues.newIntakes || []),
  ];

  // validate default new intake in create mode
  if (
    isCreateMode &&
    stepValues?.isDefaultNewIntakeDeleted !== true &&
    !stepValues?.defaultNewIntakes?.[0]?.deadline &&
    !stepValues?.defaultNewIntakes?.[0]?.startDate
  ) {
    errors.defaultNewIntakes[0] = {
      startDate: "Start Date is required",
      deadline: "Deadline is required",
    };
  }

  // check start dates duplications
  const allStartDates = allIntakes.reduce((startDates, intake) => {
    return intake?.startDate?.isValid()
      ? [...startDates, moment(intake.startDate).format(DATE_FORMATS.frontEnd)]
      : startDates;
  }, []);

  if (new Set(allStartDates).size !== allStartDates.length) {
    const message = "There are intakes with the same start date";
    errors.intake = message;
    notifyMessages.push(message);
  }

  // validate intakes
  validateIntakeGroup(stepValues.existingIntakes, "existingIntakes", errors);
  validateIntakeGroup(stepValues.newIntakes, "newIntakes", errors);
  isCreateMode &&
    validateIntakeGroup(
      stepValues.defaultNewIntakes,
      "defaultNewIntakes",
      errors,
    );

  // Branches -----------------------------------------------------
  if (!stepValues.branches || stepValues.branches.length === 0) {
    const message = "Please select at least one branch";
    errors.branches = message;
    notifyMessages.push(message);
  }

  if (
    errors.existingIntakes.length > 0 ||
    errors.defaultNewIntakes.length > 0 ||
    errors.newIntakes.length > 0 ||
    Object.keys(errors).length - 3 > 0
  ) {
    throw {
      [WIZARD_ERROR_MESSAGE]: notifyMessages.join("\n\n"),
      [activeStepName]: errors,
    };
  }

  try {
    const parsedData = parseToCheckDuplicationModel(
      values.academicId,
      stepValues,
      allIntakes,
      values.id,
    );
    await backendValidator!(parsedData);
  } catch (error) {
    const { message } = mutateErrors(error);

    throw {
      [WIZARD_ERROR_MESSAGE]: message,
    };
  }
};

export default validator;
